import { addDays, isSameDay, isWithinRange, startOfDay, userDate } from 'owa-datetime';
import {
    formatUserDate,
    formatUserTime,
    formatWeekDayShortUserDate,
    formatWeekDayTime,
    formatUserDateTime,
    formatWeekDayDateTime,
} from 'owa-datetime-formatters';
import { observableToday } from 'owa-observable-datetime';
import type { OwaDate } from 'owa-datetime';
import { isReceivedColumnWide, isSingleLineListView } from 'owa-mail-layout';

export interface MailListItemDate {
    displayText: string;
    tooltipText: string;
}

export default function formatRelativeDateForMailListRow(
    date: OwaDate | string | null | undefined
): MailListItemDate {
    if (!date) {
        return {
            displayText: '',
            tooltipText: '',
        };
    }
    const isMCL = isSingleLineListView();

    const dateInUserTz = userDate(date);
    const formatter = isMCL
        ? getRelativeDateFormatterForMailListMCL(dateInUserTz, observableToday())
        : getRelativeDateFormatter(dateInUserTz, observableToday());

    return {
        displayText: formatter(dateInUserTz),
        tooltipText: formatWeekDayDateTime(dateInUserTz),
    };
}

function getRelativeDateFormatter(date: OwaDate, today: OwaDate) {
    const day = startOfDay(date);

    if (isSameDay(day, today)) {
        return formatUserTime;
    }

    const isInLastNDays = (n: number) => isWithinRange(day, addDays(today, -n), today);

    if (isInLastNDays(3)) {
        return formatWeekDayTime;
    }

    if (isInLastNDays(30)) {
        return formatWeekDayShortUserDate;
    }

    return formatUserDate;
}

// This function returns the appropriate relative date formatter for the mail list MCL view.
// The Received column in MCL can be resized to be wider, so we use a different formatter when
// the column is wide enough to accommodate both date and time.
function getRelativeDateFormatterForMailListMCL(date: OwaDate, today: OwaDate) {
    const day = startOfDay(date);

    // For same day, always show just the time.
    if (isSameDay(day, today)) {
        return formatUserTime;
    }

    const isInLastNDays = (n: number) => isWithinRange(day, addDays(today, -n), today);

    // For dates within the last 1 day, show the day of week and the time.
    if (isInLastNDays(1)) {
        return formatWeekDayTime;
    }

    // For all other dates, if the received column is wide, show both the full date and time. If it is narrow, show just the full date.
    return isReceivedColumnWide() ? formatUserDateTime : formatUserDate;
}
